import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, IconButton, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { find, findIndex, isEmpty } from 'lodash';
import { getIn, useFormikContext } from 'formik';
import PostAddRoundedIcon from '@material-ui/icons/PostAddRounded';

import { DeleteIcon, SocialAccountIcon, ValidatedCheckbox, ValidatedTextField } from 'components';
import { Content, Network } from 'api';
import { useClient } from 'hooks';
import { hasUrl } from 'features/content/helpers';
import {
  BITLY_OFFSET,
  FACEBOOK_LIMIT,
  LINKEDIN_LIMIT,
  TWITTER_LIMIT,
  DISCLOSURE_SPACE_COUNT,
} from 'constants/index';

import { getDisclosureNetwork } from '../../helpers';
import { usePostContext } from '../../PostContext';
import { TextCounter } from '../';

import { Switch } from '../';
import Widget from './WidgetContainer';

const useStyles = makeStyles(theme => ({
  icon: {
    marginLeft: theme.spacing(2),
  },
  textField: {
    backgroundColor: theme.palette.background.default,
  },
  disclosure: {
    padding: theme.spacing(0, 2, 1, 2),
  },
  textLimitReached: {
    color: theme.palette.error.main,
    padding: theme.spacing(0, 2, 1, 2),
  },
}));

const Commentary: FC = () => {
  const classes = useStyles();
  const { values, setFieldValue } = useFormikContext<Content>();
  const { disclosures } = useClient();
  const { state, dispatch } = usePostContext();
  const { t } = useTranslation();

  function indexOfNetwork(network: Network) {
    const commentary = values.commentary || [];
    let index = findIndex(commentary, ['network', network]);
    if (index < 0) {
      index = commentary.length;
      setFieldValue(`commentary[${index}]`, { network, commentary: '' });
    }
    return index;
  }

  function getLimit(network: Network) {
    let limit = 0;

    if (!limit) {
      switch (network) {
        case 'twitter':
          limit = TWITTER_LIMIT;
          limit = hasUrl(values) ? limit - BITLY_OFFSET : limit;
          break;
        case 'linkedin':
          limit = LINKEDIN_LIMIT;
          break;
        case 'facebook':
          limit = FACEBOOK_LIMIT;
          break;
      }
    }

    return limit;
  }

  function getActiveDisclosure(network: Network) {
    const disclosureNetwork = getDisclosureNetwork(network);
    const contentDisclosure = find(values.disclosures, item => item.network === disclosureNetwork);

    return contentDisclosure || { network: disclosureNetwork, disclosure_id: '' };
  }

  function onChangeSwitch() {
    if (state.commentary) {
      setFieldValue(`commentary.0`, { network: Network.Twitter, commentary: '' });
      setFieldValue(`commentary.1`, { network: Network.Linkedin, commentary: '' });
      setFieldValue(`commentary.2`, { network: Network.Facebook, commentary: '' });
      setFieldValue('lock_commentary', false);
    }

    dispatch({ type: 'commentary' });
  }

  function renderNetwork(network: Network) {
    const index = indexOfNetwork(network);
    const fieldName = `commentary[${index}].commentary`;
    const value = getIn(values, fieldName) || '';
    let limit = getLimit(network);

    const contentDisclosure = getActiveDisclosure(network);
    const disclosure = find(disclosures, item => item.id === contentDisclosure.disclosure_id);
    const hasDisclosure = !isEmpty(disclosure);

    if (disclosure) {
      limit = limit - disclosure.disclosure.length - DISCLOSURE_SPACE_COUNT;
    }

    return (
      <Grid container alignItems="center">
        <Grid item xs={1}>
          <SocialAccountIcon active={!!value || !!disclosure} provider={network} />
        </Grid>
        <Grid item xs={10}>
          <Grid container direction="column" className={classes.textField}>
            <Grid item>
              <ValidatedTextField
                name={fieldName}
                i18nKey={`posting:${network}Commentary`}
                TextFieldProps={{ label: null, margin: 'none', multiline: true, rows: 3 }}
                InputProps={{
                  endAdornment: <TextCounter length={value.length} limit={limit} />,
                }}
                onChange={(event, onChange) => onChange(event)}
              />
            </Grid>
            {hasDisclosure && (
              <Grid item>
                <Typography className={classes.disclosure}>{disclosure.disclosure}</Typography>
              </Grid>
            )}
            {value.length > limit && (
              <Grid item>
                <Typography className={classes.textLimitReached}>
                  {t('posting:shareCopyTruncate')}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Grid>
        {!!value && (
          <Grid item xs={1}>
            <IconButton
              onClick={() => setFieldValue(fieldName, '')}
              size="small"
              className={classes.icon}>
              <DeleteIcon />
            </IconButton>
          </Grid>
        )}
      </Grid>
    );
  }

  function renderBody() {
    return (
      <Grid container>
        <Grid item xs={1} />
        <Grid item xs={11} container direction="column" spacing={2} wrap="nowrap">
          <Grid item xs>
            {renderNetwork(Network.Twitter)}
          </Grid>
          <Grid item xs>
            {renderNetwork(Network.Linkedin)}
          </Grid>
          <Grid item xs>
            {renderNetwork(Network.Facebook)}
          </Grid>
          <Grid item xs container alignItems="center">
            <Grid item xs={1} />
            <Grid item>
              <ValidatedCheckbox
                name="lock_commentary"
                fullWidth
                i18nKey="posting:lockCommentary"
                color="primary"
                onChange={event => {
                  setFieldValue('lock_commentary', !event.target.checked);
                }}
                checkedFn={() => !values.lock_commentary}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid item xs>
      <Widget
        titleKey="posting:commentaryTitle"
        subtitleKey="posting:guidelineCommentary"
        icon={<PostAddRoundedIcon color={state.commentary ? 'inherit' : 'disabled'} />}
        toggle={<Switch checked={state.commentary} onChange={onChangeSwitch} color="primary" />}>
        {state.commentary && renderBody()}
      </Widget>
    </Grid>
  );
};

export default Commentary;
